import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MessageSquare, Save, Eye, EyeOff, Lock, CheckCircle2, Search, Trash2 } from "lucide-react";

const EMPTY = { account_sid: "", auth_token: "", phone_number: "", verify_service_sid: "", messaging_service_sid: "" };

export const TwilioConfigForm = ({ cfg, onChanged }) => {
  const [form, setForm] = useState(EMPTY);
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [nums, setNums] = useState(null);
  const [loadingNums, setLoadingNums] = useState(false);

  useEffect(() => {
    if (!cfg) return;
    setForm({ account_sid: cfg.account_sid || "", auth_token: "", phone_number: cfg.phone_number || "",
              verify_service_sid: cfg.verify_service_sid || "", messaging_service_sid: cfg.messaging_service_sid || "" });
  }, [cfg]);

  const loadNumbers = async () => {
    setLoadingNums(true);
    try {
      const { data } = await api.get("/admin/twilio/numbers");
      setNums(data);
      if (data.ok) toast.success(`${data.numbers.length} numéro(s) trouvé(s) · ${data.sms_capable_count} compatible(s) SMS`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoadingNums(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/twilio/settings", form);
      toast.success(data.enabled
        ? `Twilio configuré et vérifié${data.resend?.scheduled ? ` — renvoi de ${data.resend.pending} SMS en attente lancé` : ""}`
        : "Configuration enregistrée (token chiffré). Ajoutez un numéro ou un Messaging Service pour activer l'envoi.");
      setForm((f) => ({ ...f, auth_token: "" }));
      await onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm("Effacer la configuration Twilio ? Les SMS repasseront en mode « en attente ».")) return;
    setClearing(true);
    try {
      await api.delete("/admin/twilio/settings");
      toast.success("Configuration Twilio effacée");
      setNums(null);
      await onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setClearing(false); }
  };

  return (
    <div className="p-5 bg-white border border-[#DEE2E6]" data-testid="tw-config-form">
      <div className="font-display font-bold text-lg mb-3 flex items-center gap-2">
        <MessageSquare className="h-5 w-5 text-[#0070BA]" />Identifiants Twilio
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <Label className="text-[10px] uppercase tracking-wider">Account SID *</Label>
          <div className="text-[10px] text-[#868E96] mb-1">Commence par <code>AC…</code> — vérifié chez Twilio à l'enregistrement</div>
          <Input value={form.account_sid} onChange={(e) => setForm({ ...form, account_sid: e.target.value })} placeholder="AC..." data-testid="tw-sid" />
        </div>
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Label className="text-[10px] uppercase tracking-wider">Auth Token *</Label>
            <span data-testid="tw-token-encrypted" className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider bg-[#059669] text-white px-1.5 py-0.5">
              <Lock className="h-2.5 w-2.5" /> Chiffré Fernet
            </span>
          </div>
          <div className="text-[10px] text-[#868E96] mb-1">
            {cfg?.has_auth_token ? <>Actuel : <code className="text-[#0A0A0A]">{cfg.auth_token_masked}</code>. Laissez vide pour conserver.</> : "Aucun token."}
          </div>
          <div className="relative">
            <Input type={showToken ? "text" : "password"} value={form.auth_token} onChange={(e) => setForm({ ...form, auth_token: e.target.value })} placeholder="********************************" data-testid="tw-token" />
            <button type="button" onClick={() => setShowToken(!showToken)} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#495057]" data-testid="tw-token-toggle">
              {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-[10px] uppercase tracking-wider">Numéro d'envoi (E.164) — facultatif</Label>
            <Button size="sm" variant="outline" onClick={loadNumbers} disabled={loadingNums || !cfg?.account_sid} data-testid="tw-detect-numbers" className="h-6 text-[9px] uppercase tracking-wider">
              <Search className="h-3 w-3 mr-1" />{loadingNums ? "Recherche…" : "Détecter"}
            </Button>
          </div>
          <div className="text-[10px] text-[#868E96] mb-1">Laissez vide si vous utilisez un Messaging Service. Ex : <code>+33756781234</code></div>
          <Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} placeholder="+33..." data-testid="tw-phone" />
          {nums?.ok === false && nums?.reason && (
            <div className="mt-1 text-[10px] text-[#7F1D1D] bg-[#FEF2F2] border border-[#FCA5A5] p-1.5" data-testid="tw-numbers-error">{nums.reason}</div>
          )}
          {nums?.numbers?.length > 0 && (
            <div className="mt-2 border border-[#DEE2E6] divide-y divide-[#DEE2E6] max-h-40 overflow-y-auto" data-testid="tw-numbers-list">
              {nums.numbers.map((n) => (
                <button key={n.sid} type="button" data-testid={`tw-pick-${n.sid}`}
                        onClick={() => { setForm({ ...form, phone_number: n.phone_number }); toast.success(`${n.phone_number} sélectionné`); }}
                        className={`w-full text-left p-2 hover:bg-[#F8F9FA] flex items-center gap-2 ${form.phone_number === n.phone_number ? "bg-[#ECFDF5]" : "bg-white"}`}>
                  <span className="font-mono text-xs font-bold">{n.phone_number}</span>
                  <span className="text-[10px] text-[#868E96] flex-1 truncate">{n.friendly_name}</span>
                  {n.sms
                    ? <span className="text-[9px] uppercase font-bold bg-[#059669] text-white px-1.5 py-0.5">SMS</span>
                    : <span className="text-[9px] uppercase font-bold bg-[#868E96] text-white px-1.5 py-0.5">voix seule</span>}
                  {form.phone_number === n.phone_number && <CheckCircle2 className="h-3.5 w-3.5 text-[#059669]" />}
                </button>
              ))}
            </div>
          )}
          {nums?.ok && nums.numbers.length === 0 && (
            <div className="mt-1 text-[10px] text-[#F59E0B]" data-testid="tw-numbers-empty">Aucun numéro sur ce compte Twilio — achetez-en un dans la console, ou utilisez un Messaging Service.</div>
          )}
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-wider">Messaging Service SID (alternative au numéro)</Label>
          <div className="text-[10px] text-[#868E96] mb-1">Commence par <code>MG</code> — remplace le numéro d'envoi</div>
          <Input value={form.messaging_service_sid} onChange={(e) => setForm({ ...form, messaging_service_sid: e.target.value })} placeholder="MG..." data-testid="tw-msg-sid" />
          {nums?.messaging_services?.length > 0 && (
            <div className="mt-2 border border-[#DEE2E6] divide-y divide-[#DEE2E6] max-h-32 overflow-y-auto" data-testid="tw-services-list">
              {nums.messaging_services.map((s) => (
                <button key={s.sid} type="button" data-testid={`tw-pick-svc-${s.sid}`}
                        onClick={() => { setForm({ ...form, messaging_service_sid: s.sid }); toast.success(`${s.friendly_name} sélectionné`); }}
                        className="w-full text-left p-2 hover:bg-[#F8F9FA] bg-white">
                  <span className="text-xs font-semibold">{s.friendly_name}</span>
                  <span className="block font-mono text-[9px] text-[#868E96]">{s.sid}</span>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase tracking-wider">Verify Service SID (facultatif — 2FA/OTP)</Label>
          <div className="text-[10px] text-[#868E96] mb-1">Commence par <code>VA</code></div>
          <Input value={form.verify_service_sid} onChange={(e) => setForm({ ...form, verify_service_sid: e.target.value })} placeholder="VA..." data-testid="tw-verify-sid" />
        </div>
      </div>
      <div className="mt-4 flex gap-2 flex-wrap items-center">
        <Button onClick={save} disabled={saving} data-testid="tw-save" className="bg-[#0070BA] text-white">
          <Save className="h-4 w-4 mr-1" />{saving ? "Vérification chez Twilio…" : "Enregistrer"}
        </Button>
        {(cfg?.account_sid || cfg?.has_auth_token) && (
          <Button variant="outline" onClick={clear} disabled={clearing} data-testid="tw-clear" className="text-[#E60000] border-[#E60000]/40">
            <Trash2 className="h-4 w-4 mr-1" />{clearing ? "Effacement…" : "Effacer la configuration"}
          </Button>
        )}
        <span className="text-[10px] text-[#868E96]">À l'enregistrement, les SMS en attente sont renvoyés automatiquement.</span>
      </div>
    </div>
  );
};
